import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import {
  ConfigurationService,
  SETTING_KEYS,
  SettingValue,
} from '../../features/configuration/configuration.service';

/**
 * Client-level feature switches read from Control Panel > Configuration
 * (e.g. `b2b.enabled`). Loaded once after login and again whenever the
 * Configuration page saves, so a module appears in the menu as soon as an
 * admin turns it on. `version` is a signal so computed menus re-evaluate.
 *
 * Until the settings have loaded every gated module is hidden; a failed
 * fetch keeps them hidden too (the server enforces the same switch).
 */
@Injectable({ providedIn: 'root' })
export class ModuleFlagsService {
  private config = inject(ConfigurationService);
  private auth = inject(AuthService);

  private flags: Map<string, SettingValue> | null = null;
  private pending$: Observable<boolean> | null = null;
  readonly version = signal(0);

  readonly KEYS = {
    B2B_ENABLED: SETTING_KEYS.B2B_ENABLED,
  };

  constructor() {
    this.auth.isLoggedIn$.subscribe((loggedIn) => {
      if (loggedIn) this.reload();
      else this.clear();
    });
    this.config.changed$.subscribe(() => this.reload());
  }

  /** Resolves once the flags are loaded (true) or the fetch failed (false). */
  ensureLoaded(): Observable<boolean> {
    if (this.flags) return of(true);
    if (!this.auth.isLoggedIn()) return of(false);
    if (this.pending$) return this.pending$;
    this.pending$ = this.config.getSettings().pipe(
      tap((res) => this.set(new Map(res.settings.map((s) => [s.key, s.value])))),
      map(() => true),
      catchError(() => {
        this.pending$ = null;
        return of(false);
      }),
      shareReplay(1),
    );
    return this.pending$;
  }

  isLoaded(): boolean {
    return this.flags !== null;
  }

  /** True only when the flag is loaded and switched on. */
  isEnabled(key: string): boolean {
    return this.flags?.get(key) === true;
  }

  reload(): void {
    this.pending$ = null;
    this.flags = null;
    this.ensureLoaded().subscribe();
  }

  clear(): void {
    this.flags = null;
    this.pending$ = null;
    this.version.update((n) => n + 1);
  }

  private set(flags: Map<string, SettingValue>): void {
    this.flags = flags;
    this.version.update((n) => n + 1);
  }
}
